import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  ExternalLink,
  FileText,
  Info,
  Link2,
  Loader2,
  MonitorUp,
  RefreshCw,
  Server,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { worstStatus } from './systemStatusSeverity';
import { componentChipLabel } from './systemStatusLabels';
import {
  whatsappSystemStatusService,
  type WhatsAppSystemStatus,
  type WhatsAppSystemStatusLevel,
} from '@/services/whatsappSystemStatusService';
import {
  portalAutomationStatusService,
  type PortalAutomationStatus,
} from '@/services/portalAutomationStatusService';
import {
  openRouterAccountObservabilityService,
  type OpenRouterOperationalStatus,
} from '@/services/openRouterAccountObservabilityService';
import { deriveOpenRouterHealth } from '@/services/openRouterHealth';
import {
  supplierDocsBehind,
  supplierDocsBehindSentence,
  supplierDocsChipSummary,
} from './supplierDocsBehind';
import {
  supplierDocumentPipelineStatusService,
  type SupplierDocumentPipelineStatus,
} from '@/services/supplierDocumentPipelineStatusService';

const REFRESH_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 20_000;

/**
 * The bar carries a TINT, not a shout.
 *
 * These were saturated full-width blocks — `bg-red-600 text-white` across the whole
 * strip as soon as any one dependency went stale. In production that produced a solid
 * red line reading "WhatsApp healthy · Fora stale · Supplier docs degraded · OpenRouter
 * unknown": three different states, one alarm colour, and the healthy primary channel
 * painted the same as the broken one. A bar that looks identical whether one thing or
 * everything is wrong cannot be triaged at a glance, which is the only thing a status
 * bar is for.
 *
 * The worst state still tints the strip, so a problem is still visible without reading.
 * Which component has the problem is carried by the per-component chips below.
 */
const barClasses: Record<WhatsAppSystemStatusLevel, string> = {
  healthy: 'border-emerald-200 bg-emerald-50 text-emerald-950 hover:bg-emerald-100',
  degraded: 'border-amber-200 bg-amber-50 text-amber-950 hover:bg-amber-100',
  stale: 'border-red-200 bg-red-50 text-red-950 hover:bg-red-100',
  unknown: 'border-slate-200 bg-slate-50 text-slate-900 hover:bg-slate-100',
};

/** One chip per dependency, coloured by ITS OWN state — so working parts read as working. */
const chipClasses: Record<WhatsAppSystemStatusLevel, string> = {
  healthy: 'border-emerald-300 bg-emerald-100 text-emerald-900',
  degraded: 'border-amber-300 bg-amber-100 text-amber-950',
  stale: 'border-red-300 bg-red-100 text-red-900',
  unknown: 'border-slate-300 bg-slate-100 text-slate-700',
};

const chipDotClasses: Record<WhatsAppSystemStatusLevel, string> = {
  healthy: 'bg-emerald-600',
  degraded: 'bg-amber-500',
  stale: 'bg-red-600',
  unknown: 'bg-slate-400',
};

const panelClasses: Record<WhatsAppSystemStatusLevel, string> = {
  healthy: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  degraded: 'border-amber-200 bg-amber-50 text-amber-950',
  stale: 'border-red-200 bg-red-50 text-red-900',
  unknown: 'border-slate-200 bg-slate-50 text-slate-800',
};

function StatusIcon({ status, className }: { status: WhatsAppSystemStatusLevel; className?: string }) {
  if (status === 'healthy') return <CheckCircle2 className={className} aria-hidden="true" />;
  if (status === 'stale') return <XCircle className={className} aria-hidden="true" />;
  if (status === 'degraded') return <AlertTriangle className={className} aria-hidden="true" />;
  return <Activity className={className} aria-hidden="true" />;
}

function formatTimestamp(value?: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/London',
    timeZoneName: 'short',
  }).format(date);
}

function formatAge(value?: string | null, now = Date.now()) {
  if (!value) return 'never';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return 'unknown';
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function asNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDuration(secondsValue: unknown) {
  const seconds = Math.max(0, asNumber(secondsValue));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.floor(seconds % 60)}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// Render a scheduling cadence (minutes between runs) as a human phrase. null =>
// the worker has no active cron. The RPC derives whole-hour and whole-day
// cadences (e.g. 720 for the 12-hourly canonical worker) so they read as hours
// or days rather than an awkward minute count.
function formatCadence(minutes: number | null): string {
  if (minutes == null) return 'Not scheduled';
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? 'Every 24 hours' : `Every ${days} days`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `Every ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `Every ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

function Metric({ label, value, note }: { label: string; value: number | string; note?: string }) {
  return (
    <div className="border border-gray-200 bg-white p-3">
      <div className="text-xl font-bold text-gray-950">{typeof value === 'number' ? value.toLocaleString('en-GB') : value}</div>
      <div className="mt-0.5 text-xs font-medium text-gray-700">{label}</div>
      {note && <div className="mt-1 text-[11px] text-gray-500">{note}</div>}
    </div>
  );
}

function DetailRow({ label, value, warning = false }: { label: string; value: string; warning?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-2 text-sm last:border-b-0">
      <span className="text-gray-600">{label}</span>
      <strong className={cn('max-w-[65%] text-right text-gray-950', warning && 'text-amber-800')}>{value}</strong>
    </div>
  );
}

function statusErrorText(error: unknown) {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'object' && error && 'message' in error
      ? String(error.message)
      : 'WhatsApp status check failed.';
  if (message.toLowerCase().includes('super admin')) return 'This diagnostic is restricted to super administrators.';
  if (message.toLowerCase().includes('abort')) return 'The WhatsApp status check timed out.';
  return message;
}

export function WhatsAppSystemStatusBar({ compactOnMobile = false }: { compactOnMobile?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<WhatsAppSystemStatus | null>(null);
  const [portalStatus, setPortalStatus] = useState<PortalAutomationStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [openRouter, setOpenRouter] = useState<OpenRouterOperationalStatus | null>(null);
  const [openRouterError, setOpenRouterError] = useState<string | null>(null);
  const [supplierPipeline, setSupplierPipeline] = useState<SupplierDocumentPipelineStatus | null>(null);
  const [supplierPipelineError, setSupplierPipelineError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [failbackLoading, setFailbackLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const requestRef = useRef<Promise<void> | null>(null);

  const loadStatus = useCallback((silent = false) => {
    if (requestRef.current) return requestRef.current;
    if (!silent) setLoading(true);

    const request = (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const [whatsAppResult, portalResult, openRouterResult, supplierPipelineResult] = await Promise.allSettled([
          whatsappSystemStatusService.getStatus(controller.signal),
          portalAutomationStatusService.getStatus(controller.signal),
          openRouterAccountObservabilityService.getStatus(controller.signal),
          supplierDocumentPipelineStatusService.getStatus(controller.signal),
        ]);
        if (whatsAppResult.status === 'fulfilled') {
          setStatus(whatsAppResult.value);
          setError(null);
        } else {
          setError(statusErrorText(whatsAppResult.reason));
        }
        if (portalResult.status === 'fulfilled') {
          setPortalStatus(portalResult.value);
          setPortalError(null);
        } else {
          setPortalError(statusErrorText(portalResult.reason).replace('WhatsApp', 'Fora automation'));
        }
        if (openRouterResult.status === 'fulfilled') {
          setOpenRouter(openRouterResult.value);
          setOpenRouterError(null);
        } else {
          setOpenRouterError(statusErrorText(openRouterResult.reason).replace('WhatsApp', 'OpenRouter'));
        }
        if (supplierPipelineResult.status === 'fulfilled') {
          setSupplierPipeline(supplierPipelineResult.value);
          setSupplierPipelineError(null);
        } else {
          setSupplierPipelineError(statusErrorText(supplierPipelineResult.reason).replace('WhatsApp', 'Supplier documents'));
        }
        setNow(Date.now());
      } finally {
        window.clearTimeout(timeout);
        setLoading(false);
      }
    })();

    requestRef.current = request;
    void request.finally(() => {
      if (requestRef.current === request) requestRef.current = null;
    });
    return request;
  }, []);

  useEffect(() => {
    void loadStatus();
    const refreshTimer = window.setInterval(() => void loadStatus(true), REFRESH_INTERVAL_MS);
    const ageTimer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      window.clearInterval(refreshTimer);
      window.clearInterval(ageTimer);
    };
  }, [loadStatus]);

  const openForaViewer = useCallback(async () => {
    setViewerLoading(true);
    setPortalError(null);
    const popup = window.open('', '_blank');
    if (popup) {
      popup.opener = null;
      popup.document.title = 'Opening secure Fora browser…';
      popup.document.body.textContent = 'Creating a short-lived secure viewer link…';
    }
    try {
      const link = await portalAutomationStatusService.createViewerLink();
      if (popup) popup.location.replace(link.viewer_url);
      else window.open(link.viewer_url, '_blank', 'noopener,noreferrer');
    } catch (viewerError) {
      if (popup) popup.close();
      setPortalError(statusErrorText(viewerError).replace('WhatsApp', 'Fora automation'));
    } finally {
      setViewerLoading(false);
    }
  }, []);

  const failbackToPrimary = useCallback(async () => {
    if (!window.confirm(
      'Return Fora automation to the desktop primary? Azure will be fenced and stopped before the desktop profile starts.',
    )) return;
    setFailbackLoading(true);
    setPortalError(null);
    try {
      await portalAutomationStatusService.failbackToPrimary();
      await loadStatus();
    } catch (failbackError) {
      setPortalError(statusErrorText(failbackError).replace('WhatsApp', 'Fora automation'));
    } finally {
      setFailbackLoading(false);
    }
  }, [loadStatus]);

  const whatsAppLevel: WhatsAppSystemStatusLevel = error ? 'unknown' : status?.status || 'unknown';
  const foraLevel: WhatsAppSystemStatusLevel = portalError ? 'unknown' : portalStatus?.status || 'unknown';
  const openRouterHealth = deriveOpenRouterHealth({
    operationalStatus: openRouter,
    error: openRouterError,
  });
  const openRouterLevel: WhatsAppSystemStatusLevel = openRouterHealth.level;
  const supplierPipelineLevel: WhatsAppSystemStatusLevel = supplierPipelineError
    ? 'unknown'
    : supplierPipeline?.status || 'unknown';
  const level = worstStatus(whatsAppLevel, foraLevel, openRouterLevel, supplierPipelineLevel);
  // Which part of the supplier-documents pipeline is behind, and when it last had a good run (not just "degraded").
  const supplierBehindParts = supplierPipelineError ? [] : supplierDocsBehind(supplierPipeline);
  const supplierBehindSentence = supplierDocsBehindSentence(supplierBehindParts);
  const componentStatuses: Array<{ name: string; level: WhatsAppSystemStatusLevel; label: string; help: string }> = [
    {
      name: 'WhatsApp',
      level: whatsAppLevel,
      label: componentChipLabel('WhatsApp', whatsAppLevel),
      help: 'The client-messaging channel the platform ingests. Green means new WhatsApp messages are arriving and being processed; red or amber means the feed is stale or the WhatsApp bridge is down.',
    },
    {
      name: 'Fora',
      level: foraLevel,
      // "Fora: bookings OK · quote test failing" rather than a blanket "Fora degraded" when only the quote test fails.
      label: componentChipLabel('Fora', foraLevel, portalError ? null : portalStatus?.summary_label),
      help: 'The automated browser that signs into the Fora partner portal to pull hotel and cruise bookings and their commission. Green means it is signed in and running; red means it needs attention. "Bookings OK · quote test failing" means sign-in and the booking sync work but the monitoring quote test does not.',
    },
    {
      name: 'Supplier docs',
      level: supplierPipelineLevel,
      label: componentChipLabel('Supplier docs', supplierPipelineLevel, supplierDocsChipSummary(supplierBehindParts)),
      help: `The pipeline that ingests supplier invoices and e-tickets from email and checks with Sabre/Amadeus that each PNR (airline booking) was actually ticketed.${supplierBehindSentence ? ` Behind right now: ${supplierBehindSentence}` : ''}`,
    },
    {
      name: 'OpenRouter',
      level: openRouterLevel,
      label: componentChipLabel('OpenRouter', openRouterLevel),
      help: 'The AI model gateway used for message triage and drafting replies. Green means the AI models are responding normally.',
    },
  ];
  const whatsAppHeadline = error || status?.headline || 'Checking WhatsApp ingestion';
  const foraHeadline = portalError || portalStatus?.headline || 'Checking Fora automation';
  const openRouterLabel = openRouterHealth.label;
  const supplierPipelineHeadline = supplierPipelineError || supplierPipeline?.headline || 'Checking supplier documents';
  const headline = `${whatsAppHeadline} · ${foraHeadline} · ${supplierPipelineHeadline}`;
  const unavailableComponents = [
    error ? 'WhatsApp' : null,
    portalError ? 'Fora' : null,
    supplierPipelineError ? 'Supplier documents' : null,
    openRouterError ? 'OpenRouter' : null,
  ].filter((name): name is string => Boolean(name));
  const compactHeadline = unavailableComponents.length
    ? error ? 'WhatsApp status unavailable' : 'System status unavailable'
    : headline;
  // Below 1280 px (main area under ~1024 px with the sidebar) the four chips do not fit on one line beside "View
  // details" and the last one was cut off: one short summary instead, details on click.
  const needsAttention = componentStatuses.filter((component) => component.level !== 'healthy');
  const narrowSummary = unavailableComponents.length
    ? compactHeadline
    : needsAttention.length === 0
      ? 'All systems healthy'
      : `${needsAttention.length} of ${componentStatuses.length} not healthy: ${needsAttention.map((component) => component.name).join(', ')}`;
  const lastMessageLabel = useMemo(
    () => formatAge(status?.latest_message.created_at, now),
    [now, status?.latest_message.created_at],
  );
  const heartbeatLabel = useMemo(
    () => formatAge(status?.heartbeat.checked_at, now),
    [now, status?.heartbeat.checked_at],
  );
  const ticketPipelineLabel = useMemo(
    () => formatAge(supplierPipeline?.ticket_ingestion.last_completed_at, now),
    [now, supplierPipeline?.ticket_ingestion.last_completed_at],
  );
  const queueDeadLetters = asNumber(status?.queue.dead_letter_24h);
  const queuePending = asNumber(status?.queue.queued) + asNumber(status?.queue.running) + asNumber(status?.queue.retrying);
  const viewerWorker = portalStatus?.workers.find(
    (worker) => worker.worker_key === portalStatus.active_worker_key,
  )
    || portalStatus?.workers.find((worker) => worker.role === 'primary')
    || portalStatus?.workers[0]
    || null;
  const primaryWorker = portalStatus?.executor.active === 'self_hosted' ? viewerWorker : null;
  const primaryHost = portalStatus?.workers.find((worker) => worker.role === 'primary') || null;
  const standbyHost = portalStatus?.workers.find((worker) => worker.role === 'standby') || null;
  const workerHeartbeatLabel = formatAge(primaryWorker?.last_heartbeat_at, now);
  const primaryHeartbeatMs = primaryHost?.last_heartbeat_at
    ? new Date(primaryHost.last_heartbeat_at).getTime()
    : 0;
  const primaryColdReady = Boolean(
    primaryHost
      && primaryHost.activation_state === 'fenced'
      && primaryHost.browser_state === 'stopped'
      && primaryHost.status === 'healthy'
      && Number.isFinite(primaryHeartbeatMs)
      && primaryHeartbeatMs > 0
      && now - primaryHeartbeatMs <= 90_000,
  );
  const canFailback = portalStatus?.failover?.state === 'failing_back' || primaryColdReady;
  const uccGapInWindow = status
    ? status.projection.ucc_gap_status_window_days === 7
      ? status.projection.ucc_work_source_identity_missing_7d
      : status.projection.ucc_work_source_identity_missing_30d
    : null;
  const hasUnresolvedBridgeError = Boolean(
    status?.heartbeat.detail.last_error
      && (!status.heartbeat.detail.last_success_at
        || !status.heartbeat.detail.last_error_at
        || new Date(status.heartbeat.detail.last_error_at).getTime()
          > new Date(status.heartbeat.detail.last_success_at).getTime()),
  );

  const componentAriaSummary = componentStatuses
    .map((component) => `${component.label}. ${component.help}`)
    .join(' ');

  return (
    <TooltipProvider delayDuration={150}>
      {/*
        One slim line with a FIXED height (h-10), never wrapping (CRM release C: the strip used to
        wrap to two or three rows, 60-100 px, before any page content). The fixed height is also the
        reserved-height strategy: the chip labels ("WhatsApp unknown" while loading vs "WhatsApp
        healthy"/"WhatsApp stale" once resolved) and the right-hand "WA … · tickets … · OpenRouter …"
        text change length at every refresh, and a strip whose height follows its content shoved all
        page content by ~16-20px on load and on every 30s tick, so coordinate-based clicks landed on
        the wrong element. Below 1280 px one summary replaces the chips (four chips and "View details"
        need about 750 px); from 1280 px the chips show in full. Nothing wraps; the full detail is one
        click away. Do not go back to a content-driven height.
      */}
      <button
        type="button"
        data-app-top-chrome=""
        onClick={() => setOpen(true)}
        className={cn(
          'sticky top-16 z-20 flex h-10 w-full items-center justify-between gap-3 overflow-hidden border-b px-4 text-left shadow-sm transition-colors lg:top-0 lg:pr-64 xl:pr-72',
          barClasses[level],
        )}
        aria-label={`${headline}. Last WhatsApp message ${lastMessageLabel}. Supplier ticket ingestion ${ticketPipelineLabel}. ${componentAriaSummary} Open full system details.`}
      >
        <span className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-hidden">
          {loading && !status
            ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
            : <StatusIcon status={level} className="h-4 w-4 shrink-0" />}
          <span className="min-w-0 truncate text-xs font-semibold xl:hidden">{compactOnMobile ? compactHeadline : narrowSummary}</span>
          {componentStatuses.map((component) => (
            <Tooltip key={component.name}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'flex shrink-0 cursor-help items-center gap-1.5 whitespace-nowrap rounded border px-2 py-0.5 text-xs font-semibold max-xl:hidden',
                    chipClasses[component.level],
                  )}
                >
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', chipDotClasses[component.level])} aria-hidden="true" />
                  {component.label}
                  <Info className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-left">{component.help}</TooltipContent>
            </Tooltip>
          ))}
        </span>
        <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium">
          <span className="hidden 2xl:inline">WA {lastMessageLabel} · tickets {ticketPipelineLabel} · OpenRouter {openRouterLabel}</span>
          {/* bg-white/15 here was tuned for the old saturated dark bar; on the tinted
              strip it renders as an invisible affordance. */}
          <span className="rounded border border-current/30 bg-white/70 px-2 py-0.5">View details</span>
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto bg-gray-50 sm:max-w-4xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" aria-hidden="true" />
              Operations system status
            </SheetTitle>
            <SheetDescription>
              Live WhatsApp, supplier-document, GDS verification and persistent Fora automation evidence. Message content, supplier payloads and credentials are excluded.
            </SheetDescription>
          </SheetHeader>

          <div className={cn('my-5 flex flex-wrap items-center justify-between gap-3 border p-4', panelClasses[level])}>
            <div className="flex items-start gap-3">
              <StatusIcon status={level} className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="font-semibold">{headline}</div>
                <div className="mt-1 text-xs opacity-80">
                  WhatsApp heartbeat {heartbeatLabel} · supplier tickets {ticketPipelineLabel} · Fora worker {workerHeartbeatLabel} · OpenRouter {openRouterLabel}
                </div>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => void loadStatus()} disabled={loading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} aria-hidden="true" />
              Refresh now
            </Button>
          </div>

          {(error || portalError || openRouterError || supplierPipelineError) && (
            <div role="alert" className="mb-5 border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <strong>One or more status checks need attention.</strong>{' '}
              {[error, portalError, openRouterError, supplierPipelineError].filter(Boolean).join(' · ')}
            </div>
          )}

          {supplierPipeline && (
            <section className="mb-5" aria-labelledby="supplier-pipeline-heading">
              <h3 id="supplier-pipeline-heading" className="mb-3 flex items-center gap-2 font-semibold text-gray-950">
                <FileText className="h-4 w-4" aria-hidden="true" />
                Supplier documents and live PNR verification
              </h3>
              {supplierBehindSentence && (
                <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950" title="Which part of the pipeline is behind, and when it last finished a good run (UK time).">
                  <span className="font-semibold">Behind right now: </span>{supplierBehindSentence}
                </p>
              )}
              <div className="grid gap-3 lg:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Invoice Gmail + parsing</CardTitle></CardHeader>
                  <CardContent>
                    <DetailRow label="Schedule" value={`Every ${supplierPipeline.schedules.invoice_minutes} minutes`} />
                    <DetailRow label="Last completed" value={formatTimestamp(supplierPipeline.invoice_ingestion.last_completed_at)} warning={supplierPipeline.invoice_ingestion.last_status !== 'completed'} />
                    <DetailRow label="Pending parse" value={supplierPipeline.invoice_ingestion.pending.toLocaleString('en-GB')} warning={supplierPipeline.invoice_ingestion.pending > 10} />
                    <DetailRow label="Parse errors" value={supplierPipeline.invoice_ingestion.parse_errors.toLocaleString('en-GB')} warning={supplierPipeline.invoice_ingestion.parse_errors > 0} />
                    <DetailRow label="Retired (no file)" value={(supplierPipeline.invoice_ingestion.terminal_file_missing ?? 0).toLocaleString('en-GB')} />
                    <DetailRow label="Rejected ticket values" value={supplierPipeline.invoice_ingestion.invalid_ticket_values.toLocaleString('en-GB')} warning={supplierPipeline.invoice_ingestion.invalid_ticket_values > 0} />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Ticket PDFs</CardTitle></CardHeader>
                  <CardContent>
                    <DetailRow label="Schedule" value={`Every ${supplierPipeline.schedules.ticket_minutes} minutes`} />
                    <DetailRow label="Last completed" value={formatTimestamp(supplierPipeline.ticket_ingestion.last_completed_at)} warning={supplierPipeline.ticket_ingestion.last_status !== 'completed'} />
                    {/* A work list, not a fault: a number, never a warning (it no longer degrades the status). */}
                    <DetailRow label="To review (unmatched / needs review)" value={supplierPipeline.ticket_ingestion.unmatched_or_review.toLocaleString('en-GB')} />
                    <DetailRow label="Failed" value={supplierPipeline.ticket_ingestion.failed.toLocaleString('en-GB')} warning={supplierPipeline.ticket_ingestion.failed > 0} />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Sabre / Amadeus live read</CardTitle></CardHeader>
                  <CardContent>
                    <DetailRow label="Schedule" value={formatCadence(supplierPipeline.schedules.gds_minutes)} warning={supplierPipeline.schedules.gds_minutes == null} />
                    <DetailRow label="Last completed" value={formatTimestamp(supplierPipeline.gds_verification.last_completed_at)} warning={supplierPipeline.gds_verification.last_status !== 'completed'} />
                    <DetailRow label="Overdue checks (current bookings)" value={supplierPipeline.gds_verification.overdue_candidates.toLocaleString('en-GB')} warning={supplierPipeline.gds_verification.overdue_candidates > 25} />
                    {supplierPipeline.gds_verification.excluded_departed != null && (
                      <DetailRow label={`Not counted: departed over ${supplierPipeline.gds_verification.departed_grace_days ?? 30} days ago`} value={supplierPipeline.gds_verification.excluded_departed.toLocaleString('en-GB')} />
                    )}
                    {supplierPipeline.gds_verification.excluded_unreadable != null && (
                      <DetailRow label="Not counted: PNR purged or refused, trip already departed" value={supplierPipeline.gds_verification.excluded_unreadable.toLocaleString('en-GB')} />
                    )}
                    {supplierPipeline.gds_verification.unreadable_no_date != null && (
                      <DetailRow label="Not counted: PNR purged or refused, no departure date" value={supplierPipeline.gds_verification.unreadable_no_date.toLocaleString('en-GB')} warning={supplierPipeline.gds_verification.unreadable_no_date > 0} />
                    )}
                    {supplierPipeline.gds_verification.newly_unreadable_24h != null && (
                      <DetailRow label="Became unreadable in the last 24h" value={supplierPipeline.gds_verification.newly_unreadable_24h.toLocaleString('en-GB')} warning={supplierPipeline.gds_verification.newly_unreadable_24h > 10} />
                    )}
                    {supplierPipeline.gds_verification.last_successful_read_at !== undefined && (
                      <DetailRow label="Last successful live read" value={formatTimestamp(supplierPipeline.gds_verification.last_successful_read_at)} warning={Boolean(supplierPipeline.gds_verification.read_outage)} />
                    )}
                    <DetailRow label="Supported" value={supplierPipeline.gds_verification.supports.join(' + ')} />
                  </CardContent>
                </Card>
              </div>
              <div className="mt-3 text-right">
                <Link to="/finance/supplier-invoices" className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900">
                  Open supplier documents <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            </section>
          )}

          {portalStatus && (
            <section className="mb-5" aria-labelledby="fora-automation-heading">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h3 id="fora-automation-heading" className="flex items-center gap-2 font-semibold text-gray-950">
                  <MonitorUp className="h-4 w-4" aria-hidden="true" />
                  Persistent Fora automation
                </h3>
                <div className="flex flex-wrap gap-2">
                  {['failed_over', 'failing_back', 'promotion_failed'].includes(portalStatus.failover?.state || '') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void failbackToPrimary()}
                      disabled={failbackLoading || !canFailback}
                      title={canFailback ? 'Drain the standby and return to the desktop primary' : 'The fenced desktop host must be online and stopped before failback'}
                    >
                      {failbackLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                      Return to desktop primary
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void openForaViewer()}
                    disabled={viewerLoading || !viewerWorker?.viewer_available}
                  >
                    {viewerLoading
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                      : <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />}
                    Open secure browser
                  </Button>
                </div>
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between gap-3 text-base">
                      <span className="flex items-center gap-2"><Server className="h-4 w-4" aria-hidden="true" />Executor and login</span>
                      <Badge variant="outline" className={panelClasses[portalStatus.status]}>{portalStatus.status}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Headline" value={portalStatus.headline} warning={portalStatus.status !== 'healthy'} />
                    <DetailRow label="Preferred executor" value={portalStatus.executor.preferred === 'self_hosted' ? 'Persistent Docker browser' : 'Managed Steel'} />
                    <DetailRow label="Active executor" value={portalStatus.executor.active === 'self_hosted' ? 'Persistent Docker browser' : portalStatus.executor.active === 'steel' ? 'Managed Steel' : 'None'} warning={!portalStatus.executor.viable} />
                    <DetailRow label="Managed fallback active" value={portalStatus.executor.fallback_activated ? 'Yes' : 'No'} warning={Boolean(portalStatus.executor.fallback_activated)} />
                    <DetailRow label="Persistent failover state" value={`${portalStatus.failover?.state || 'normal'} · generation ${asNumber(portalStatus.failover?.generation)}`} warning={Boolean(portalStatus.failover?.state && portalStatus.failover.state !== 'normal')} />
                    <DetailRow label="Fallback readiness" value={portalStatus.fallback?.enabled ? `${portalStatus.fallback.executor || 'fallback'} · ${portalStatus.fallback.ready ? 'ready' : portalStatus.fallback.status || 'unavailable'}` : 'Disabled'} warning={Boolean(portalStatus.fallback?.enabled && !portalStatus.fallback?.ready)} />
                    <DetailRow label="Active worker" value={primaryWorker ? `${primaryWorker.display_name} (${primaryWorker.role})` : portalStatus.executor.active === 'steel' ? 'Managed executor' : 'Not registered'} warning={!primaryWorker && portalStatus.executor.active !== 'steel'} />
                    <DetailRow label="Active fence" value={primaryWorker ? `${primaryWorker.activation_state || 'unknown'} · generation ${asNumber(primaryWorker.fencing_generation)}` : 'Not applicable'} warning={Boolean(primaryWorker && primaryWorker.activation_state !== 'active')} />
                    <DetailRow label="Worker heartbeat" value={formatTimestamp(primaryWorker?.last_heartbeat_at)} warning={portalStatus.executor.active === 'self_hosted' && !primaryWorker?.last_heartbeat_at} />
                    <DetailRow label="Primary host" value={primaryHost ? `${primaryHost.display_name} · ${primaryHost.activation_state || 'unknown'} · ${formatAge(primaryHost.last_heartbeat_at, now)}` : 'Not registered'} warning={!portalStatus.primary_ready} />
                    <DetailRow label="Standby host" value={standbyHost ? `${standbyHost.display_name} · ${standbyHost.activation_state || 'unknown'} · ${formatAge(standbyHost.last_heartbeat_at, now)}` : 'Not registered'} warning={!portalStatus.standby_ready} />
                    <DetailRow label="Restored profile snapshot" value={formatTimestamp(primaryWorker?.profile_snapshot_at)} />
                    <DetailRow label="Browser" value={primaryWorker?.browser_state || 'unknown'} warning={portalStatus.executor.active === 'self_hosted' && primaryWorker?.browser_state !== 'running'} />
                    <DetailRow label="Fora authentication" value={primaryWorker?.auth_state || portalStatus.profile.status || 'unknown'} warning={portalStatus.executor.active === 'self_hosted' && primaryWorker?.auth_state !== 'authenticated'} />
                    <DetailRow label="Watchdog" value={primaryWorker ? `${primaryWorker.watchdog_state || 'unknown'} · ${primaryWorker.watchdog_layer || 'no failed layer'} · ${asNumber(primaryWorker.watchdog_restart_count)} restarts` : 'Not applicable to the managed active executor'} warning={Boolean(primaryWorker && ['degraded', 'escalated'].includes(primaryWorker.watchdog_state || ''))} />
                    <DetailRow label="Last authenticated" value={formatTimestamp(primaryWorker?.last_authenticated_at)} />
                    <DetailRow label="Image" value={primaryWorker?.image_version || 'Not reported'} />
                    <DetailRow label="Last encrypted backup" value={primaryWorker ? `${formatTimestamp(primaryWorker.last_backup_at)} · age ${formatDuration(primaryWorker.backup_age_seconds)}` : 'Not applicable to the managed active executor'} warning={Boolean(primaryWorker && (!primaryWorker.last_backup_at || asNumber(primaryWorker.backup_age_seconds) > 28800))} />
                    <DetailRow label="Last restore validation" value={primaryWorker ? `${primaryWorker.last_restore_test_status || 'unknown'} · ${formatTimestamp(primaryWorker.last_restore_test_at)}` : 'Not applicable to the managed active executor'} warning={Boolean(primaryWorker && primaryWorker.last_restore_test_status !== 'passed')} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base"><Database className="h-4 w-4" aria-hidden="true" />Queue and supplier proof</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Queued" value={asNumber(portalStatus.queue.queued).toLocaleString('en-GB')} warning={asNumber(portalStatus.queue.oldest_queued_seconds) > 300} />
                    <DetailRow label="Retrying" value={asNumber(portalStatus.queue.retrying).toLocaleString('en-GB')} warning={asNumber(portalStatus.queue.retrying) > 0} />
                    <DetailRow label="Running" value={asNumber(portalStatus.queue.running).toLocaleString('en-GB')} />
                    <DetailRow label="Dead letter" value={asNumber(portalStatus.queue.dead_letter).toLocaleString('en-GB')} warning={asNumber(portalStatus.queue.dead_letter) > 0} />
                    <DetailRow label="Completed (24h)" value={asNumber(portalStatus.queue.done_24h).toLocaleString('en-GB')} />
                    <DetailRow label="Failed (24h)" value={asNumber(portalStatus.queue.failed_24h).toLocaleString('en-GB')} warning={asNumber(portalStatus.queue.failed_24h) > 0} />
                    <DetailRow label="Oldest queued" value={formatDuration(portalStatus.queue.oldest_queued_seconds)} warning={asNumber(portalStatus.queue.oldest_queued_seconds) > 300} />
                    <DetailRow label="Active lease" value={portalStatus.active_lease.purpose ? `${String(portalStatus.active_lease.purpose)} · ${String(portalStatus.active_lease.claimed_by || 'worker')} · ${formatDuration(portalStatus.active_lease.lease_remaining_seconds)} remaining` : 'None'} />
                    <DetailRow label="Quote canary" value={portalStatus.last_quote_canary.finished_at ? `${portalStatus.last_quote_canary.succeeded ? 'passed' : portalStatus.last_quote_canary.failure_code || portalStatus.last_quote_canary.status || 'failed'} · ${asNumber(portalStatus.last_quote_canary.offer_count)} offers · ${formatDuration(asNumber(portalStatus.last_quote_canary.latency_ms) / 1000)}` : 'Not run yet'} warning={!portalStatus.last_quote_canary.succeeded} />
                    <DetailRow label="Canary captured" value={formatTimestamp(portalStatus.last_quote_canary.source_captured_at || portalStatus.last_quote_canary.finished_at)} />
                    {portalStatus.quote_test && (
                      <DetailRow
                        label="Quote test"
                        value={portalStatus.quote_test.state === 'failing'
                          ? `Failing since ${formatTimestamp(portalStatus.quote_test.failing_since)} · ${asNumber(portalStatus.quote_test.failures_since_last_pass).toLocaleString('en-GB')} failed runs · last passed ${formatTimestamp(portalStatus.quote_test.last_passed_at)}`
                          : portalStatus.quote_test.state === 'passing' ? 'Passing' : 'Not run yet'}
                        warning={portalStatus.quote_test.state === 'failing'}
                      />
                    )}
                    {portalStatus.booking_sync && (
                      <DetailRow
                        label="Login and booking sync"
                        value={portalStatus.booking_sync.state === 'ok'
                          ? `OK · last complete capture ${formatTimestamp(portalStatus.booking_sync.last_complete_capture_at)}`
                          : 'Needs attention'}
                        warning={portalStatus.booking_sync.state !== 'ok'}
                      />
                    )}
                    <DetailRow label="Last booking acceptance" value={portalStatus.last_booking_acceptance.ended_at ? `${String(portalStatus.last_booking_acceptance.status)} · ${asNumber(portalStatus.last_booking_acceptance.bookings_seen)} seen` : 'No approved apply yet'} />
                    <DetailRow label="Latest source / active records" value={`${asNumber(portalStatus.reconciliation.latest_source_total)} / ${asNumber(portalStatus.reconciliation.active_record_count)}`} warning={asNumber(portalStatus.reconciliation.difference_from_latest_source) !== 0} />
                    <DetailRow label="Pending preview approval" value={portalStatus.reconciliation.pending_preview ? 'Yes' : 'No'} warning={Boolean(portalStatus.reconciliation.pending_preview)} />
                    <DetailRow label="30-day health" value={portalStatus.history_30d.samples ? `${asNumber(portalStatus.history_30d.healthy_percent).toFixed(1)}% healthy · ${asNumber(portalStatus.history_30d.samples)} samples` : 'Collecting samples'} />
                    <DetailRow label="Latest Fora sync" value={String(portalStatus.last_sync.status || 'None')} warning={Boolean(portalStatus.last_sync.status && !['succeeded', 'partial'].includes(String(portalStatus.last_sync.status)))} />
                    <DetailRow label="Bookings seen" value={asNumber(portalStatus.last_sync.bookings_seen).toLocaleString('en-GB')} />
                  </CardContent>
                </Card>
              </div>
            </section>
          )}

          {status && (
            <div className="space-y-5">
              <section aria-labelledby="wa-live-heading">
                <h3 id="wa-live-heading" className="mb-3 flex items-center gap-2 font-semibold text-gray-950">
                  <Activity className="h-4 w-4" aria-hidden="true" />
                  Live position
                </h3>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <Metric label="Messages in 24h" value={status.activity_24h.messages} />
                  <Metric label="Inbound" value={status.activity_24h.inbound} />
                  <Metric label="Outbound" value={status.activity_24h.outbound} />
                  <Metric label="Awaiting analysis" value={status.activity_24h.unanalysed} note="May include outbound or media-only rows" />
                </div>
              </section>

              <section className="grid gap-3 lg:grid-cols-2" aria-label="WhatsApp processing detail">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between gap-3 text-base">
                      <span className="flex items-center gap-2"><Server className="h-4 w-4" aria-hidden="true" />Bridge</span>
                      <Badge variant="outline" className={panelClasses[status.heartbeat.status]}>{status.heartbeat.status}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Service" value={status.heartbeat.detail.service || 'pt-whatsapp-bridge'} />
                    <DetailRow label="Connection" value={status.heartbeat.detail.connection || 'unknown'} warning={status.heartbeat.detail.connection !== 'open'} />
                    <DetailRow label="Registered" value={status.heartbeat.detail.registered ? 'Yes' : 'No'} warning={!status.heartbeat.detail.registered} />
                    <DetailRow label="Heartbeat" value={formatTimestamp(status.heartbeat.checked_at)} />
                    <DetailRow label="Captured since restart" value={asNumber(status.heartbeat.detail.messages_captured_since_start).toLocaleString('en-GB')} />
                    <DetailRow label="Started" value={formatTimestamp(status.heartbeat.detail.started_at)} />
                    <DetailRow label="Last reported error" value={status.heartbeat.detail.last_error || 'None'} warning={hasUnresolvedBridgeError} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between gap-3 text-base">
                      <span className="flex items-center gap-2"><Database className="h-4 w-4" aria-hidden="true" />Latest captured message</span>
                      <Badge variant="outline">{status.latest_message.direction || 'unknown'}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Stored" value={formatTimestamp(status.latest_message.created_at)} />
                    <DetailRow label="WhatsApp timestamp" value={formatTimestamp(status.latest_message.message_at)} />
                    <DetailRow label="Database delay" value={`${asNumber(status.latest_message.ingestion_delay_seconds)}s`} />
                    <DetailRow label="Source" value={status.latest_message.source || 'unknown'} />
                    <DetailRow label="AI analysis" value={status.latest_message.analysed ? 'Complete' : 'Pending/not required'} />
                    <DetailRow label="Latest bridge run" value={formatTimestamp(status.bridge_run.created_at)} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base"><Clock3 className="h-4 w-4" aria-hidden="true" />Triage and queue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Latest finished sweep" value={formatTimestamp(status.triage.completed_at)} />
                    {status.triage.in_flight_since && (
                      <DetailRow label="Sweep running since" value={formatTimestamp(status.triage.in_flight_since)} />
                    )}
                    <DetailRow label="Sweep status" value={status.triage.status || 'unknown'} warning={status.triage.status !== 'COMPLETED'} />
                    <DetailRow label="Analysed in latest sweep" value={status.triage.messages_analysed.toLocaleString('en-GB')} />
                    <DetailRow label="Failures" value={status.triage.failed_messages.toLocaleString('en-GB')} warning={status.triage.failed_messages > 0} />
                    <DetailRow label="Pending/retry jobs" value={queuePending.toLocaleString('en-GB')} warning={queuePending > 0} />
                    <DetailRow label="Dead letters (24h)" value={queueDeadLetters.toLocaleString('en-GB')} warning={queueDeadLetters > 0} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between gap-3 text-base">
                      <span className="flex items-center gap-2"><Link2 className="h-4 w-4" aria-hidden="true" />Downstream linkage</span>
                      <Badge variant="outline" className={status.linkage.status === 'healthy' ? panelClasses.healthy : panelClasses.degraded}>
                        {status.linkage.status}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Verified identities" value={status.linkage.verified_identities.toLocaleString('en-GB')} />
                    <DetailRow label="Pending identity review" value={status.linkage.pending_identities.toLocaleString('en-GB')} warning={status.linkage.pending_identities > 0} />
                    <DetailRow label="Unmatched identities" value={status.linkage.unmatched_identities.toLocaleString('en-GB')} warning={status.linkage.unmatched_identities > 0} />
                    <DetailRow label="Open action items" value={status.linkage.open_action_items.toLocaleString('en-GB')} />
                    <DetailRow label="Contact-linked messages (24h)" value={status.activity_24h.contact_linked.toLocaleString('en-GB')} />
                    {/* Informational only: since PR 1051 WhatsApp chats create no Freshdesk tickets unless switched on, so 0 is expected. */}
                    <DetailRow label="Freshdesk-linked messages (24h)" value={status.activity_24h.freshdesk_linked.toLocaleString('en-GB')} />
                    <DetailRow label="Canonical CRM events" value={(status.projection?.canonical_events ?? 0).toLocaleString('en-GB')} />
                    <DetailRow label="UCC WhatsApp threads" value={(status.projection?.ucc_whatsapp_threads ?? 0).toLocaleString('en-GB')} />
                    <DetailRow label="UCC WhatsApp messages" value={(status.projection?.ucc_whatsapp_messages ?? 0).toLocaleString('en-GB')} />
                    <DetailRow label="Freshdesk context eligible" value={(status.projection?.freshdesk_context_eligible_threads ?? 0).toLocaleString('en-GB')} />
                    <DetailRow label="Verified LID aliases" value={(status.projection?.verified_lid_aliases ?? 0).toLocaleString('en-GB')} warning={(status.projection?.verified_lid_aliases ?? 0) === 0 && status.linkage.unmatched_identities > 0} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between gap-3 text-base">
                      <span className="flex items-center gap-2"><Database className="h-4 w-4" aria-hidden="true" />WhatsApp projection coverage</span>
                      <Badge variant="outline" className={panelClasses[uccGapInWindow == null ? 'unknown' : uccGapInWindow > 0 ? 'degraded' : status.projection.status]}>
                        {uccGapInWindow == null ? 'UCC coverage unknown' : uccGapInWindow > 0 ? 'UCC source gap' : status.projection.status}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label="Projected messages (24h)" value={status.projection.canonical_events_24h.toLocaleString('en-GB')} />
                    <DetailRow label="Missing canonical events (24h)" value={status.projection.unprojected_messages_24h.toLocaleString('en-GB')} warning={status.projection.unprojected_messages_24h > 0} />
                    <DetailRow label="Bridge to event coverage" value={`${(status.projection.projection_coverage_24h * 100).toFixed(1)}%`} warning={status.projection.projection_coverage_24h < 1} />
                    <DetailRow label="WORK inbound IDs missing UCC source identity (24h)" value={status.projection.ucc_work_source_identity_missing_24h?.toLocaleString('en-GB') ?? 'Not measured'} warning={status.projection.ucc_work_source_identity_missing_24h == null || status.projection.ucc_work_source_identity_missing_24h > 0} />
                    {status.projection.ucc_gap_status_window_days === 7 && (
                      <DetailRow label="WORK inbound IDs missing UCC source identity (7d)" value={status.projection.ucc_work_source_identity_missing_7d?.toLocaleString('en-GB') ?? 'Not measured'} warning={uccGapInWindow == null || uccGapInWindow > 0} />
                    )}
                    {/* The 30-day count is a number, not a status, once the 7-day window is live: a closed gap stays in it for a month. */}
                    <DetailRow label="WORK inbound IDs missing UCC source identity (30d)" value={status.projection.ucc_work_source_identity_missing_30d?.toLocaleString('en-GB') ?? 'Not measured'} warning={status.projection.ucc_gap_status_window_days === 30 && (uccGapInWindow == null || uccGapInWindow > 0)} />
                    <p className="mt-2 text-xs text-gray-500">WORK is a screening label. Counts are source-ID checks, not verified client tasks; personal and group chats are excluded.</p>
                    <DetailRow label="Last projected message" value={formatTimestamp(status.projection.last_projected_message_at)} />
                    <DetailRow label="UCC WhatsApp threads" value={status.projection.ucc_whatsapp_threads.toLocaleString('en-GB')} />
                    <DetailRow label="Customer-linked threads" value={status.projection.customer_linked_threads.toLocaleString('en-GB')} warning={status.projection.customer_linked_threads === 0} />
                    <DetailRow label="Verified LID aliases" value={status.projection.verified_lid_aliases.toLocaleString('en-GB')} />
                  </CardContent>
                </Card>
              </section>

              <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 bg-white p-3 text-xs text-gray-600">
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" aria-hidden="true" />Super-admin diagnostic · no message, supplier or credential content</span>
                <Link to="/ops/inbox?source=whatsapp" className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-900">
                  Open WhatsApp Ops Inbox <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
              <p className="text-xs text-gray-500">
                WhatsApp evidence {formatTimestamp(status.generated_at)}
                {portalStatus?.generated_at ? ` · Fora evidence ${formatTimestamp(portalStatus.generated_at)}` : ''}
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}
